import { clamp, Scalar } from 'geometrik'
import { PFFT } from 'pretty-fast-fft'
import { Stabilizer } from './util/stabilizer'
import { Deco } from './editor/editor'

// async function createAnalyser(blockSize: number) {
//   const fftAnalyser = await createRunner(blockSize)
//   return {

//   }
// }

// const fftSize = FFT_BLOCK_SIZE / 2

// const fftAnalyserPromise = createRunner(FFT_BLOCK_SIZE)
// let fftAnalyser: PFFT | undefined
// fftAnalyserPromise.then((x) => {
//   fftAnalyser = x
// })

const floatsStepsMap: WeakMap<Float32Array, Float32Array[]> = new WeakMap()

// const cosSin = new Float64Array(FFT_BLOCK_SIZE)
// for (let k = 0, x, p, cos, sin; k < fftSize; k++) {
//   x = (Math.min(1, k / (fftSize / 2 - 1)) ** .5) * Math.PI
//   p = k * 2
//   cos = Math.cos(x)
//   sin = Math.sin(x)
//   cosSin[p] = cos
//   cosSin[p + 1] = sin
// }

interface ColorStop {
  value: number
  color: { r: number, g: number, b: number }
}

interface Slice {
  floats: Float32Array
  colorStops: ColorStop[]
}

export class Plot<T = any> {
  canvas: HTMLCanvasElement = document.createElement('canvas')
  c: CanvasRenderingContext2D = this.canvas.getContext('2d', { alpha: true })!

  width = 0
  height = 0
  step = 1
  pixelRatio = 1
  lastFloats?: Float32Array
  floats: Float32Array
  stabilizer = new Stabilizer()

  meta?: T
  deco?: Deco

  chunks: number
  colorStops: ColorStop[]

  constructor(private analyser: PFFT, length: number = 2048) {
    this.floats = new Float32Array(length)
    this.chunks = length / analyser.blockSize - 1
    this.colorStops = Array.from({ length: this.chunks },
      () => ({ value: 0, color: { r: 0, g: 0, b: 0 } }))
  }

  declare onDraw?: () => void

  initial = true
  stabilize = false
  normalize = false

  resize(
    width: number,
    height: number,
    pixelRatio: number,
  ) {
    if (
      width !== this.width
      || height !== this.height
      || pixelRatio !== this.pixelRatio
    ) {
      const { canvas, c } = this

      this.initial = false
      this.width = width
      this.height = height
      this.pixelRatio = pixelRatio

      canvas.width = width * this.pixelRatio
      canvas.height = height * this.pixelRatio
      canvas.style.imageRendering = 'pixelated'
      c.imageSmoothingEnabled = false

      c.scale(this.pixelRatio, this.pixelRatio)

      c.lineWidth = 2
      c.lineJoin = 'bevel'

      this.clear()

      if (this.lastFloats) {
        this.draw(this.lastFloats)
      }
    }
  }

  clear() {
    this.c.clearRect(0, 0, this.width, this.height)
  }

  slices: Slice[] = []

  append(slice: Slice) {
    this.slices.push(slice)
    if (this.slices.length > 100) this.slices.shift()

    this.clear()
    this.drawSlices()
    // const { c, width, height, pixelRatio: pr } = this

    // let { total } = this

    // const w = width / total
    // total++
    // const nw = w * total
    // const co = width / nw

    // const ow = width * co

    // c.drawImage(
    //   this.canvas,
    //   0,
    //   0,
    //   width * pr,
    //   height * pr,
    //   0,
    //   0,
    //   ow,
    //   height,
    // )

    // c.drawImage(
    //   plot.canvas,
    //   0,
    //   0,
    //   plot.width * pr,
    //   plot.height * pr,
    //   ow,
    //   0,
    //   width - ow,
    //   height,
    // )

    // this.total = total
  }

  drawSlices() {
    const { c, width, height, step, pixelRatio: pr, colorStops, analyser, stabilize, stabilizer, normalize, chunks, slices } = this

    const sliceWidth = width / slices.length
    // console.log(sliceWidth)

    let i = 0
    let offsetX = 0
    // console.log(slices)
    for (const { floats, colorStops } of slices) {
      const coeff = floats.length / sliceWidth

      const gradient = c.createLinearGradient(offsetX, 0, sliceWidth, 0)
      // console.log(colorStops)

      out: {
        for (const { value: pos, color: { r, g, b } } of colorStops) {
          const rgb = `rgb(${r},${g},${b})`

          if (rgb.includes('NaN') || rgb.includes('Infinity')) {
            c.strokeStyle = '#fff'
            break out
          }

          const offset = Math.max(
            0,
            Math.min(
              1,
              new Scalar(pos)
                .scaleSelf(width)
                // .transformSelf(viewMatrix!)
                .normalizeSelf(width).x
            )
          )

          if (isFinite(offset)) {
            gradient.addColorStop(offset, rgb)
          }
        }

        c.strokeStyle = gradient
      }

      let x = 0, y, h
      h = floats[x * coeff | 0] * 0.5 + 0.5

      y = (height - pr) * h + pr / 2
      c.beginPath()
      c.moveTo(x + offsetX, y)
      x++
      for (; x < sliceWidth; x += .5) {
        h = floats[x * coeff | 0] * 0.5 + 0.5
        y = (height - pr) * h + pr / 2
        c.lineTo(x + offsetX, y)
      }
      c.stroke()

      i++
      offsetX += sliceWidth
    }
  }

  draw(newFloats: Float32Array) {
    this.lastFloats = newFloats

    const { c, width, height, step, pixelRatio: pr, colorStops, analyser, stabilize, stabilizer, normalize, chunks } = this
    const { cosSin, blockSize, fftSize } = analyser

    let floats: Float32Array

    if (stabilize) {
      floats = this.floats
      const startIndex = stabilizer.findStartingPoint(newFloats)
      floats
        .subarray(0, floats.length - startIndex)
        .set(newFloats.subarray(startIndex))
      floats
        .subarray(floats.length - startIndex)
        .set(newFloats.subarray(0, startIndex))
    }
    else {
      floats = newFloats
    }

    const coeff = floats.length / (width * (stabilize ? 2 : 1))

    let floatsSteps = floatsStepsMap.get(floats)

    if (!floatsSteps) {
      const stepSize = Math.floor(
        (floats.length - blockSize) / chunks
      )

      floatsSteps = Array.from({ length: chunks }, (_, i) => {
        const p = i * stepSize
        return floats.subarray(p, p + blockSize)
      })

      floatsStepsMap.set(floats, floatsSteps)
    }

    let fix_r = 0
    let fix_g = 0

    for (let i = 0, samples: Float32Array, fft: Float32Array, cs, r, g, b, max; i < chunks; i++) {
      samples = floatsSteps[i]
      fft = analyser.samples
      fft.set(samples)
      analyser.process(samples)

      r = g = b = 0

      for (let k = 0, p, real, imag, m, rn, gn, bn; k < fftSize; k++) {
        p = k * 2

        rn = cosSin[p]
        gn = cosSin[p + 1]
        bn = -rn

        real = fft[p]
        imag = fft[p + 1]
        m = real * real + imag * imag

        r += m * rn
        g += m * gn
        b += m * bn
      }

      max = Math.max(r, g, b)

      r /= max
      g /= max
      b /= max

      fix_r = (clamp(0, 1, 1 - (1 + (b + g))) ** 5) * 8
      fix_g = (clamp(0, 1, 1 - (1 + (b + g))) ** 10)

      cs = colorStops[i]
      cs.value = i / (this.chunks - 1)
      cs.color.r = clamp(0, 1, b + fix_r) * 255
      cs.color.g = clamp(0, 1, g + fix_g) * 255
      cs.color.b = clamp(0, 1, r) * 255
    }

    let scaleY: number = 1

    const gradient = c.createLinearGradient(0, 0, width, 0)
    // console.log(colorStops)

    out: {
      for (const { value: pos, color: { r, g, b } } of colorStops) {
        const rgb = `rgb(${r},${g},${b})`

        if (rgb.includes('NaN') || rgb.includes('Infinity')) {
          c.strokeStyle = '#fff'
          break out
        }

        const offset = Math.max(
          0,
          Math.min(
            1,
            new Scalar(pos)
              .scaleSelf(width)
              // .transformSelf(viewMatrix!)
              .normalizeSelf(width).x
          )
        )

        if (isFinite(offset)) {
          gradient.addColorStop(offset, rgb)
        }
      }

      c.strokeStyle = gradient
    }

    if (normalize) {
      let min = 0
      let max = 0
      for (let i = 0, s: number; i < floats.length; i++) {
        s = floats[i]
        if (s < min) min = s
        if (s > max) max = s
      }
      scaleY = 0.9 / Math.max(Math.abs(min), Math.abs(max))
    }

    let x = 0, y, h
    h = floats[x * coeff | 0] * scaleY * 0.5 + 0.5
    y = (height - pr) * h + pr / 2
    c.beginPath()
    c.moveTo(x, y)
    x++
    for (; x < width; x += step) {
      h = floats[x * coeff | 0] * scaleY * 0.5 + 0.5
      y = (height - pr) * h + pr / 2
      c.lineTo(x, y)
    }
    c.stroke()

    this.onDraw?.()
  }
}
